import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import { parse } from "date-fns";
import { API_BASE_URL } from "@config/config";
import { AuthContext } from "@context/AuthContext";
import { SessionContext } from "@context/SessionContext";

export const UserContext = createContext({});

const UserContextProvider = (props) => {
    const { children } = props;
    const { isLoggedIn } = useContext(AuthContext);
    const { setIsLoadingSession } = useContext(SessionContext);

    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [purchasedTickets, setPurchasedTickets] = useState({
        current: [],
        history: [],
    });
    const [purchasedVouchers, setPurchasedVouchers] = useState([]);
    const [purchasedTicket, setPurchasedTicket] = useState(null);
    const [bookedTicketForSession, setBookedTicketForSession] = useState([]);
    const [purchasedSession, setPurchasedSession] = useState(null);

    const fetchTickets = () => {
        setIsLoading(true);
        axios
            .get(`${API_BASE_URL}/ticket/purchases`)
            .then(function(response) {
                if (response.status === 200) {
                    let tickets = response.data.data;
                    const recent = new Date();
                    recent.setDate(recent.getDate());
                    tickets = tickets.map(ticket => ({
                        ...ticket,
                        purchaseDate: parse(ticket.purchase_date, "yyyy-MM-dd HH:mm:ss", new Date()),
                        sessionDate: parse(ticket.session_date_time, "yyyy-MM-dd HH:mm:ss", new Date()),
                    }));
                    // the db query returns some duplicate values, the following line deletes the duplicates while keeping the data format
                    tickets = [...new Map(tickets.map(ticket => [ticket.ticket_id, ticket]))
                        .values()];

                    const current = tickets
                        .filter(ticket => ticket.sessionDate >= recent)
                        .sort((a, b) => b.sessionDate.getTime() - a.sessionDate.getTime());

                    const history = tickets
                        .filter(ticket => ticket.sessionDate < recent)
                        .sort((a, b) => b.sessionDate.getTime() - a.sessionDate.getTime());

                    setPurchasedVouchers(tickets);
                    setPurchasedTickets({
                        current,
                        history,
                    });
                }
                setIsLoading(false);
            })
            .catch(({ response }) => {
                if (isLoggedIn && response?.status === 401) {
                    window.location = "/";
                }
                setIsLoading(false);
            });
    };

    const fetchBookedTicketForSession = (id) => {
        setIsLoadingSession(true);
        axios
            .get(`${API_BASE_URL}/ticket/purchased-today/${id}`)
            .then(function(response) {
                setBookedTicketForSession(response.data.data);
                setIsLoadingSession(false);
            })
            .catch((error) => {
                setBookedTicketForSession([]);
                setIsLoadingSession(false);
                if (isLoggedIn && error?.response?.status === 401) {
                    window.location = "/";
                }
            });
    };

    return (
        <UserContext.Provider
            value={{
                bookedTicketForSession,
                errorMessage,
                fetchBookedTicketForSession,
                fetchTickets,
                isLoading,
                processing,
                purchasedSession,
                purchasedTicket,
                purchasedTickets,
                purchasedVouchers,
                setBookedTicketForSession,
                setErrorMessage,
                setProcessing,
                setPurchasedSession,
                setPurchasedTicket,
                setPurchasedVouchers,
                setSuccessMessage,
                successMessage,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export default UserContextProvider;
